export type MdrExpectedDateSortRow = {
  document_id: number;
  type: 'document' | 'revision' | 'file';
  revision_id?: number | null;
};

/** Date-only sort key. Blank and unparseable values sort as missing. */
export function toExpectedDateSortKey(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }

  const text = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (match) {
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Sort Project Documents rows by expected date while keeping each document's
 * revisions and files underneath it.
 *
 * AG Grid multiplies a non-zero result by -1 for descending sorts. Family
 * order (document, then revision, then files) is pre-flipped so it survives
 * that inversion. Missing dates stay last in both directions.
 */
export function compareMdrRowsByExpectedDate(
  rowA: MdrExpectedDateSortRow | undefined,
  rowB: MdrExpectedDateSortRow | undefined,
  dateKeyByDocumentId: ReadonlyMap<number, number | null>,
  isDescending: boolean,
): number {
  if (!rowA || !rowB) return 0;

  if (rowA.document_id !== rowB.document_id) {
    const keyA = dateKeyByDocumentId.get(rowA.document_id) ?? null;
    const keyB = dateKeyByDocumentId.get(rowB.document_id) ?? null;
    return compareNullableNumbersNullsLast(keyA, keyB, isDescending);
  }

  return compareSameDocumentFamily(rowA, rowB, isDescending);
}

function compareNullableNumbersNullsLast(
  a: number | null,
  b: number | null,
  isDescending: boolean,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return isDescending ? -1 : 1;
  if (b == null) return isDescending ? 1 : -1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function familyRank(row: MdrExpectedDateSortRow): number {
  if (row.type === 'document') return 0;
  if (row.type === 'revision') return 1;
  return 2;
}

function compareSameDocumentFamily(
  rowA: MdrExpectedDateSortRow,
  rowB: MdrExpectedDateSortRow,
  isDescending: boolean,
): number {
  const sameRevision = rowA.revision_id != null
    && rowA.revision_id === rowB.revision_id;
  if (sameRevision || rowA.type === 'document' || rowB.type === 'document') {
    const rankA = familyRank(rowA);
    const rankB = familyRank(rowB);
    if (rankA === rankB) return 0;
    const result = rankA < rankB ? -1 : 1;
    return isDescending ? -result : result;
  }
  return 0;
}
